/**
 * Mobile-only carousel for generic Section blocks (one slide per viewport < 750px).
 * Scoped per <section-blocks-mobile-carousel>; pagination matches They Trust Us dot style.
 */

const MQ_DESKTOP = '(min-width: 750px)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

class SectionBlocksMobileCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #pagination = null;
  /** @type {Element[]} */
  #slides = [];
  #page = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList} */
  #mqDesktop = window.matchMedia(MQ_DESKTOP);

  /** @type {number} */
  #touchStartX = 0;

  /** @type {() => void} */
  #onResize = () => this.#layout();

  /** @type {() => void} */
  #onMqChange = () => {
    this.#page = 0;
    this.#layout();
  };

  /** @type {(e: TouchEvent) => void} */
  #onTouchStart = (e) => {
    if (this.#mqDesktop.matches || !this.#viewport) return;
    this.#touchStartX = e.changedTouches[0]?.clientX ?? 0;
  };

  /** @type {(e: TouchEvent) => void} */
  #onTouchEnd = (e) => {
    if (this.#mqDesktop.matches || !this.#viewport) return;
    const total = this.#totalPages;
    if (total <= 1) return;
    const endX = e.changedTouches[0]?.clientX ?? 0;
    const dx = endX - this.#touchStartX;
    const threshold = 48;
    if (dx > threshold) this.#go(-1);
    else if (dx < -threshold) this.#go(1);
  };

  /** @type {(e: KeyboardEvent) => void} */
  #onViewportKeydown = (e) => {
    if (this.#mqDesktop.matches) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.#go(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.#go(1);
    }
  };

  connectedCallback() {
    this.#viewport = this.querySelector('[data-blocks-carousel-viewport]');
    this.#track = this.querySelector('[data-blocks-carousel-track]');
    this.#pagination = this.querySelector('[data-blocks-carousel-pagination]');

    this.#mqDesktop.addEventListener('change', this.#onMqChange);

    if (this.#viewport) {
      this.#resizeObserver = new ResizeObserver(this.#onResize);
      this.#resizeObserver.observe(this.#viewport);
      this.#viewport.addEventListener('touchstart', this.#onTouchStart, {
        passive: true,
      });
      this.#viewport.addEventListener('touchend', this.#onTouchEnd, {
        passive: true,
      });
      this.#viewport.addEventListener('keydown', this.#onViewportKeydown);
    }

    this.#layout();
  }

  disconnectedCallback() {
    this.#mqDesktop.removeEventListener('change', this.#onMqChange);
    this.#resizeObserver?.disconnect();
    this.#viewport?.removeEventListener('touchstart', this.#onTouchStart);
    this.#viewport?.removeEventListener('touchend', this.#onTouchEnd);
    this.#viewport?.removeEventListener('keydown', this.#onViewportKeydown);
  }

  /** @returns {Element[]} */
  #collectSlides() {
    const track = this.#track;
    if (!track) return [];
    return Array.from(track.children).filter((n) => n.nodeType === Node.ELEMENT_NODE);
  }

  get #slideCount() {
    return this.#slides.length;
  }

  get #slidesPerView() {
    return this.#mqDesktop.matches ? 999 : 1;
  }

  get #totalPages() {
    const n = this.#slideCount;
    if (n === 0) return 1;
    const spv = Math.min(this.#slidesPerView, n);
    if (this.#mqDesktop.matches) return 1;
    return Math.max(1, Math.ceil(n / spv));
  }

  /** @returns {number} */
  #getGapPx() {
    const track = this.#track;
    if (!track) return 12;
    const g = getComputedStyle(track).gap || getComputedStyle(track).columnGap;
    const token = String(g ?? '')
      .trim()
      .split(/\s+/)[0];
    const parsed = parseFloat(token || '');
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 12;
  }

  /**
   * @param {number} page
   */
  #goTo(page) {
    if (this.#mqDesktop.matches) return;
    if (this.#totalPages <= 1) return;
    const next = Math.max(0, Math.min(this.#totalPages - 1, page));
    if (next === this.#page) return;
    this.#page = next;
    this.#applyTransform(true);
    this.#syncAria();
    this.#syncDots(false);
  }

  /**
   * @param {number} delta — +1 or -1
   */
  #go(delta) {
    if (this.#mqDesktop.matches) return;
    if (this.#totalPages <= 1) return;
    let next = this.#page + delta;
    if (next < 0) next = this.#totalPages - 1;
    else if (next >= this.#totalPages) next = 0;
    this.#goTo(next);
  }

  /** @param {boolean} animate */
  #applyTransform(animate) {
    const viewport = this.#viewport;
    const track = this.#track;
    if (!viewport || !track) return;

    if (this.#mqDesktop.matches) {
      track.style.transform = '';
      track.style.transition = '';
      return;
    }

    const cs = getComputedStyle(viewport);
    const padX =
      (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const vw = viewport.clientWidth - padX;
    const gap = this.#getGapPx();
    const slideW = vw;

    for (const slide of this.#slides) {
      if (slide instanceof HTMLElement) {
        slide.style.flex = `0 0 ${slideW}px`;
        slide.style.minWidth = `${slideW}px`;
        slide.style.maxWidth = `${slideW}px`;
      }
    }

    const offset = this.#page * (slideW + gap);

    const reduce = window.matchMedia(REDUCED_MOTION).matches;
    if (reduce || !animate) {
      track.style.transition = 'none';
    } else {
      track.style.transition =
        'transform 0.45s cubic-bezier(0.25, 0.1, 0.25, 1)';
    }
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  #resetMobileInlineStyles() {
    const track = this.#track;
    if (!track) return;
    track.style.transform = '';
    track.style.transition = '';

    for (const slide of this.#slides) {
      if (slide instanceof HTMLElement) {
        slide.style.flex = '';
        slide.style.minWidth = '';
        slide.style.maxWidth = '';
      }
      slide.removeAttribute('aria-hidden');
    }
  }

  #syncAria() {
    if (this.#mqDesktop.matches) {
      this.#slides.forEach((slide) => slide.removeAttribute('aria-hidden'));
      return;
    }
    const start = this.#page * 1;
    this.#slides.forEach((slide, i) => {
      const visible = i >= start && i < start + 1;
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  /**
   * @param {boolean} [forceBuild]
   */
  #syncDots(forceBuild = false) {
    const host = this.#pagination;
    if (!host) return;

    if (this.#mqDesktop.matches) {
      host.setAttribute('hidden', '');
      host.innerHTML = '';
      return;
    }

    const total = this.#totalPages;
    const active = this.#page;

    const existing = host.querySelectorAll('button[data-blocks-carousel-dot]');
    if (forceBuild || existing.length !== total) {
      host.innerHTML = '';

      if (total <= 1) {
        host.setAttribute('hidden', '');
        return;
      }

      host.removeAttribute('hidden');

      const ol = document.createElement('ol');
      ol.className = 'section-blocks-mobile-carousel__dots';

      for (let i = 0; i < total; i += 1) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'section-blocks-mobile-carousel__dot';
        btn.dataset.blocksCarouselDot = String(i);
        btn.setAttribute('aria-label', `Go to slide ${i + 1} of ${total}`);
        btn.addEventListener('click', () => this.#goTo(i));
        li.appendChild(btn);
        ol.appendChild(li);
      }

      host.appendChild(ol);
    } else {
      host.toggleAttribute('hidden', total <= 1);
    }

    const dots = host.querySelectorAll('button[data-blocks-carousel-dot]');
    dots.forEach((btn, i) => {
      if (i === active) btn.setAttribute('aria-selected', 'true');
      else btn.removeAttribute('aria-selected');
    });
  }

  #layout() {
    this.#slides = this.#collectSlides();

    if (this.#mqDesktop.matches) {
      this.#resetMobileInlineStyles();
      this.#pagination?.setAttribute('hidden', '');
      if (this.#pagination) this.#pagination.innerHTML = '';
      this.#page = 0;
      return;
    }

    this.#page = Math.min(this.#page, Math.max(0, this.#totalPages - 1));

    this.#applyTransform(false);
    this.#syncAria();
    this.#syncDots(true);
  }
}

if (!customElements.get('section-blocks-mobile-carousel')) {
  customElements.define(
    'section-blocks-mobile-carousel',
    SectionBlocksMobileCarousel
  );
}
